import { useResizeObserver } from '@react-aria/utils';
import {
  Button,
  ButtonLevel,
  ButtonSize,
  FieldError,
  FieldGroup,
  FieldHelper,
  type FieldProps,
  Input,
  Label,
  ListBox,
  type ListBoxProps,
  Popover,
} from '@vizit/atoms';
import { ChevronDownIcon } from '@vizit/icons';
import { cx } from '@vizit/theme';
import * as React from 'react';
import {
  ComboBox as AriaComboBox,
  type ComboBoxProps as AriaComboBoxProps,
  type PopoverProps,
  composeRenderProps,
} from 'react-aria-components';
import { ComboboxClearButton } from './combobox.clear-button';

interface ComboBoxProps<T extends object> extends FieldProps, Omit<AriaComboBoxProps<T>, 'children'> {
  /**
   * The items of the combo box.
   */
  children: React.ReactNode | ((item: T) => React.ReactNode);
  /**
   * A ref used to connect form library to the input.
   *
   * @optional
   * @type {React.Ref<HTMLInputElement>}
   */
  inputRef?: React.Ref<HTMLInputElement>;
  /**
   * Whether there are more items to load. Used for infinite scrolling.
   *
   * @optional
   * @type {boolean}
   */
  isLoading?: boolean;
  /**
   * Props passed to the list box component.
   *
   * @optional
   * @type {ListBoxProps}
   */
  listBoxProps?: ListBoxProps<T>;
  /**
   * Props passed to the popover component.
   *
   * @optional
   * @type {PopoverProps}
   */
  popoverProps?: PopoverProps;
  /**
   * The maxHeight for the popover.
   *
   * @optional
   * @type {number}
   * @default 256
   */
  maxHeight?: number;
  /**
   * The placeholder of the input.
   *
   * @optional
   * @type {string}
   */
  placeholder?: string;
  /**
   * A ref for the scrollable region within the overlay. Used to determine if the listbox should load more items when the user scrolls.
   *
   * @default overlayRef
   */
  scrollRef?: React.RefObject<Element | null>;
  /**
   * A ref for the combo box.
   *
   * @optional
   * @type {React.Ref<HTMLDivElement>}
   */
  ref?: React.Ref<HTMLDivElement>;
  /**
   * Handler that is called when the list box is scrolled to the bottom. Used for infinite scrolling.
   *
   * @optional
   * @type {() => void}
   */
  onLoadMore?: () => void;
  /**
   * Render function for empty state when there are no items to display.
   *
   * @required
   * @type {() => React.ReactNode}
   */
  renderEmptyState: () => React.ReactNode;
}

function ComboBox<T extends object>(props: ComboBoxProps<T>) {
  const {
    children,
    className: classNameProp,
    description,
    errorMessage,
    inputRef,
    isInvalid,
    isLoading,
    isRequired,
    items,
    label,
    listBoxProps = {},
    popoverProps = {},
    maxHeight = 256,
    placeholder,
    renderEmptyState,
    scrollRef,
    onLoadMore,
    ref,
    ...other
  } = props;

  const triggerRef = React.useRef<HTMLDivElement>(null);

  const [triggerWidth, setTriggerWidth] = React.useState<string | null>(null);

  const handleResize = React.useCallback(() => {
    if (triggerRef.current) {
      setTriggerWidth(`${triggerRef.current.offsetWidth}px`);
    }
  }, []);

  useResizeObserver({ ref: triggerRef, onResize: handleResize });

  return (
    <AriaComboBox
      allowsEmptyCollection
      className={composeRenderProps(classNameProp, (className) => cx('group flex flex-col gap-4', className))}
      items={items}
      isInvalid={isInvalid}
      isRequired={isRequired}
      ref={ref}
      {...other}
    >
      <>
        {label && <Label isRequired={isRequired}>{label}</Label>}
        <FieldGroup ref={triggerRef} className="flex items-center gap-8">
          <Input
            ref={inputRef}
            placeholder={placeholder}
            className="placeholder:text-placeholder placeholder:font-regular [&:focus]:outline-none [&:focus]:ring-0 [&:focus]:shadow-none [&:focus]:border-none"
          />

          <ComboboxClearButton />
          <Button
            className="pl-0 pr-0 [&[aria-expanded=true]>.vzt-end-icon]:rotate-180"
            size={ButtonSize.Lg}
            level={ButtonLevel['Text-Secondary']}
          >
            <ChevronDownIcon
              aria-hidden="true"
              size={16}
              className="vzt-end-icon shrink-0 text-gray-utility-400 transition-transform duration-200"
            />
          </Button>
        </FieldGroup>
        {description && <FieldHelper>{description}</FieldHelper>}
        {errorMessage && isInvalid && <FieldError>{errorMessage}</FieldError>}
        <Popover
          className="w-[--trigger-width] min-w-[8rem]"
          offset={4}
          style={{ '--trigger-width': triggerWidth } as React.CSSProperties}
          triggerRef={triggerRef}
          {...popoverProps}
        >
          <ListBox
            isLoading={isLoading}
            items={items}
            renderEmptyState={renderEmptyState}
            style={{ maxHeight }}
            onLoadMore={onLoadMore}
            {...listBoxProps}
          >
            {children}
          </ListBox>
        </Popover>
      </>
    </AriaComboBox>
  );
}

export type { ComboBoxProps };
export { ComboBox };
